import { Component, computed, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LendersService } from 'src/app/services/lenders.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { Role } from 'src/app/models/user/role.model';
import { UsersService } from 'src/app/services/users.service';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { entityExistsValidator } from 'src/app/validators/entity-exists.validator';
import { IData, RowDetailsDataComponent } from 'src/app/modules/shared/components/row-detail-data/row-details-data.component';
import { LenderOrganizationsService } from 'src/app/services/lender.organizations.service';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { BtnCellRenderer } from 'src/app/modules/shared/components/btn-cell-renderer/btn-cell-renderer.component';
import { TextTooltipRenderer } from 'src/app/modules/shared/components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from 'src/app/modules/shared/utils';
import { formatPhone } from 'src/app/modules/shared/utils/phone.util';
import { PhoneInputDirective } from 'src/app/modules/shared/directives/phone-input.directive';
import { tenDigitPhoneValidator } from 'src/app/modules/shared/validators/phone.validator';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { AgGridWrapperComponent } from '../../../shared/components/ag-grid-wrapper/ag-grid-wrapper.component';
import { RightSidebarContainerComponent } from '../../../shared/components/right-sidebar-container/right-sidebar-container.component';
import { SearchSortComponent } from '../../../shared/components/search-sort/search-sort.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { BaseListComponent } from '../../../shared/components/base-list.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { StatesService } from 'src/app/services/states.service';
import { OrgStructureComponent } from '../../../shared/components/org-structure/org-structure.component';
import { OrgAssociationsComponent } from '../../../shared/components/org-associations/org-associations.component';
import { IOrganizationService } from 'src/app/models/muli-org/organization-service.model';

@Component({
    selector: 'app-lenders-list',
    templateUrl: './lenders-list.component.html',
    styleUrls: ['./lenders-list.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      MatSidenavModule,
      RightSidebarContainerComponent,
      ReactiveFormsModule,
      MatFormFieldModule,
      SearchSortComponent,
      AgGridWrapperComponent,
      MatPaginatorModule,
      MatIconModule,
      RowDetailsDataComponent,
      MatInputModule,
      MatButtonModule,
      MatTabsModule,
      MatSelectModule,
      PhoneInputDirective,
      OrgStructureComponent,
      OrgAssociationsComponent
    ]
})
export class LendersListComponent extends BaseListComponent<any> implements OnInit {
  public organizationId: any;
  @ViewChild('drawer') drawer: MatDrawer;
  @ViewChild('orgAssociations') orgAssociationsComponent?: OrgAssociationsComponent;
  
  isEditing = false;
  id: number;
  organization: any;
  descriptionRowData: IData[] = [{
    title: 'Description',
    description: '-'
  }];
  productData: any[] = [];
  datePipe = new DatePipe('en-US');

  subscriptions: Subscription[] = [];

  form: FormGroup = new FormGroup({
    email: new FormControl('', { validators: [Validators.email, Validators.required], updateOn: 'blur'}),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    phoneNumber: new FormControl('', [
      Validators.required,
      tenDigitPhoneValidator(),
    ]),
    organizationId: new FormControl('', [Validators.required]),
    role: new FormControl<number | null>(null, [Validators.required]),
  });

  roleOptions = [
    { id: 4, name: 'Full Access' },
    { id: 7, name: 'Read Only' },
  ];

  public columnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'firstName',
      flex:2,
      cellRenderer: TextTooltipRenderer,
    },
    { ...baseColDef, field: 'lastName',cellRenderer: TextTooltipRenderer, flex:2 },
    { ...baseColDef, field: 'email',cellRenderer: TextTooltipRenderer, flex:3 },
    {
      ...baseColDef,
      field: 'phoneNumber',
      cellRenderer: TextTooltipRenderer,
      flex:2,
      valueFormatter: (params) => formatPhone(params.value),
    },
    {
      ...baseColDef,
      field: 'role',
      headerName: 'Access Level',
      cellRenderer: TextTooltipRenderer,
      flex:2,
      valueFormatter: (params) => params.value === 7 ? 'Read Only' : params.value === 4 ? 'Full Access' : 'N/A',
    },
  ];

  currentPageSize: number = 20;

  sortOptions = [
    { label: 'Name', sortValue: 'firstName' },
    { label: 'Last Name', sortValue: 'lastName' },
    { label: 'Email', sortValue: 'email' },
  ];

  searchKey: string = '';

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender || this.userRole() === Role.LenderReadOnly);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider= computed(() => this.userRole() === Role.Provider);
  readonly isSystemUser= computed(() => this.userRole() === Role.SystemUser);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);

  gridAPI: GridApi;

  statesList: any[] = [];

  constructor(
    private lenderService: LendersService,
    private organizationService: LenderOrganizationsService,
    private deleteDialog: DeleteConfirmationDialogService,
    private route: ActivatedRoute,
    private statesService: StatesService,
    private userService: UsersService,
    private router: Router) {
      super();
  }

  get lenderOrganizationService(): IOrganizationService {
    return this.organizationService;
  }

  onStructureChanged() {
    if (this.orgAssociationsComponent) {
      this.orgAssociationsComponent.loadOrganizationRelationships();
    }  
  }

  ngOnInit(): void {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (this.isLender()) {
      const lenderSubscription = this.userService.organizationId$.pipe(
        tap((userOrgId) => {
          this.organizationId = routeId ? routeId : userOrgId?.toString();
        }),
        switchMap(() => {
          if (this.organizationId) {
            return this.statesService.get();
          }
          return of(null);
        }),
        tap((res) => {
          if (res) {
            this.statesList = res.data;
          }
        }),
        switchMap(() => {
          if (this.organizationId) {
            return this.organizationService.getById(this.organizationId);
          }
          return of(null);
        })
      ).subscribe((data) => {
        if (data && !data.isError) {
          this.setOrganizationData(data.data);
        }
      });

      this.subscriptions.push(lenderSubscription);
    } else {
      this.organizationId = routeId;     

      const statesSubscription = this.statesService.get().pipe(
        tap((res) => {
          this.statesList = res.data;
        }),
        tap(() => {
          if (this.organizationId) {
            this.organizationService.getById(this.organizationId).subscribe((data) => {
              if (!data.isError) {
                this.setOrganizationData(data.data);
              }
            });
          }
        })
      ).subscribe();

      this.subscriptions.push(statesSubscription);
    }
    const searchControlSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.reload();
        }),
      )
      .subscribe();

    this.subscriptions.push(searchControlSubscription);
  }

private setOrganizationData(organizationData: any) {
  this.organization = organizationData;
  const addressParts = [];
  if (this.organization.mailingStreetAddress) {
    addressParts.push(this.organization.mailingStreetAddress);
  }
  if (this.organization.mailingStreetAddressLine2) {
    addressParts.push(this.organization.mailingStreetAddressLine2);
  }
  if (this.organization.mailingCity) {
    addressParts.push(this.organization.mailingCity);
  }
  if (this.organization.mailingStateName) {
    addressParts.push(this.organization.mailingStateName);
  }
  if (this.organization.mailingZipCode) {
    addressParts.push(this.organization.mailingZipCode);
  }
  
  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '-';
  
  this.descriptionRowData = [
    {
      title: 'Organization Details',
      description: `Email: ${this.organization.email || '-'}\nPhone Number: ${formatPhone(this.organization.phoneNumber)}\nAddress: ${fullAddress}`
    }
  ];

  this.productData = [...organizationData.productIds];
  this.productData = this.productData.map((pd: any) => ({
    ...pd,
    states: this.findMissingStateIds(pd.states)
  }));
}
  openLenderSidebar(data: any = null) {
    if (data) {
      this.id = data.id;
      this.form.controls['email'].setAsyncValidators([entityExistsValidator(this.lenderService, data.email)]);
      const getSus = this.lenderService.getById(this.id.toString()).subscribe(res => {
        if(!res.isError) {
          this.form.reset({
            email: res.data.email,
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            phoneNumber: res.data.phoneNumber,
            organizationId: res.data.organizationId,
            role: data.role ?? null,
          });
        }
      })
      this.subscriptions.push(getSus);
      this.isEditing = true;
      this.form.controls['email'].disable();
      this.form.controls['role'].enable();
    } else {
      this.form.controls['organizationId'].setValue(this.organizationId);
      this.form.controls['email'].setAsyncValidators([entityExistsValidator(this.lenderService)]);
      this.form.controls['email'].enable();
      this.form.controls['role'].enable();
      this.isEditing = false;
    }
    this.form.updateValueAndValidity();
    this.drawer.toggle();
  }

  close() {
    this.form.reset();
    this.drawer.close();
  }

  onSubmit() {
    if (this.form.valid) {
      this.isLoading = true;
      const data = this.form.value;

      if (this.isEditing) {
        const updateSuscription = this.lenderService
          .update(this.id.toString(), data)
          .subscribe((data) => {
            this.reload();
            this.isLoading = false;
            this.close();
          });
          this.subscriptions.push(updateSuscription);
      } else {
        const addSuscription = this.lenderService.add(this.form.value).subscribe((data) => {
          this.reload();
          this.isLoading = false;
          this.close();
        });
        this.subscriptions.push(addSuscription);
      }
    }
  }

  onView(data: any) {
    this.router.navigate([`/admin/user-view/${data.userId}`]);
  }

  onChangeAvailability(data: any) {
    this.deleteDialog
      .openDeleteConfirmation(
        `This lender user will be ${data.isActive ? 'disabled' : 'enabled'}.`,
        data.isActive ? 'Disable confirmation' : 'Enable confirmation',
        data.isActive ? 'Disable' : 'Enable'
      )
      .afterClosed()
      .pipe(
        switchMap(dialogClosed => {
          if(dialogClosed)
            return this.lenderService.changeUserAvailability(data.userId, !data.isActive);
          else
            return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
        if (deleteConfirmation) {
          this.reload();
        }
      });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridAPI = params.api;
      this.reload();
      this.adjustColumnVisibility(params.api);
  }

  reload(backward: boolean = false) {

    this.searchKey = this.searchControl.value?.toLowerCase() || '';

    let filters: any = {
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      forward: !backward,
      pageSize: this.pageSize,
      searchKey: this.searchKey
    };

    this.gridAPI.showLoadingOverlay();
    this.lenderService
      .getByOrganizationId(this.organizationId, filters)
      .pipe(
        switchMap((response: any) => {
          if (!response.isError) {
            return of(response.data);
          } else {
            return of([]);
          }
        })
      )
      .subscribe((data: any) => {
        this.totalCount = data.totalCount;
        this.rowData = data.items;
        this.gridAPI.hideOverlay()
      });
  }

  private adjustColumnVisibility(gridApi: any) {
    if(!this.isAdmin()) return;

    const actionCol = {
      field: 'action',
      minWidth: 80,
      maxWidth: 80,
      filter: false,
      sortable: false,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        onEdit: this.isAdmin() ? this.openLenderSidebar.bind(this) : undefined,
        onChangeAvailability: this.isAdmin() ? this.onChangeAvailability.bind(this) : undefined,
      },
    };

    gridApi.setColumnDefs([...this.columnDefs, actionCol]);
  }

  private findMissingStateIds(selectedStates: any[] = []): number[] {
    const selectedIds = new Set(selectedStates.map(state => state.id));

    return this.statesList
      .filter(state => !selectedIds.has(state.id))
      .map(state => state.id);
  }

  isStateMissing(stateId: number, productStates: any[]): boolean {
    return productStates.includes(stateId);
  }



  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }
}
